/**
 * Análises (D522; doc 20 §4.3): the pure view-model under the Admin's own BI — periods, comparison, deltas, KPI extraction,
 * the trend series, the holiday warning, the freshness age and the scope the server answered for. No I/O and no clock (the
 * caller passes `now`), so every rule here is unit-tested in Node and the screens only draw what this module decides.
 *
 * Rules that live here and nowhere else:
 *  - a period is a named relative one (the SERVER resolves "today" in the markets' own clocks) or an explicit local-date range;
 *  - the default comparison is "same period last year, by weekday" and falls back to the previous period when the year before
 *    has no data (`resolveAutoCompare`);
 *  - money is NEVER summed across currencies: every figure carries its currency and a result with several currencies is shown
 *    once per currency;
 *  - a ratio is shown as the server computed it (Σnumerator ÷ Σdenominator) and its delta is in percentage POINTS;
 *  - nothing is cached: this module holds no state.
 */
import {
  type CompareAgainst,
  type CompareKind,
  type Granularity,
  type QueryFilter,
  type QueryRequest,
  type RelativePeriod,
  type ResultCell,
  type ResultHoliday,
  type ResultRow,
  type ResultTotal,
  addDays,
  inclusiveDays,
  isRealDate,
  resolveRelative,
} from '@vello/analytics-semantic';
import { type AnalyticsBiFreshness, type AnalyticsBiQueryView, type OperatingMarketView } from '@vello/api-client';
import { marketsOfRegion } from './scope.js';
import { type OperatorScope } from './shell.js';

// ───────────────────────────── period ─────────────────────────────

/** The quick presets (a button each). */
export const BI_PRESETS = ['today', 'last_7_days', 'last_30_days', 'month_to_date', 'year_to_date'] as const;
/** D528: the other relative periods, under "other periods" (weeks run Monday to Sunday). */
export const BI_MORE_PRESETS = ['yesterday', 'this_week', 'last_week', 'last_month', 'this_quarter', 'last_quarter', 'last_year'] as const;
export const BI_ALL_PRESETS = [...BI_PRESETS, ...BI_MORE_PRESETS] as const;
export type BiPreset = (typeof BI_ALL_PRESETS)[number];
/** The most days "the last N days" may span (the server's cap). */
export const BI_MAX_LAST_DAYS = 1100;
export type BiPeriod =
  | { readonly kind: 'preset'; readonly preset: BiPreset }
  | { readonly kind: 'custom'; readonly from: string; readonly to: string }
  | { readonly kind: 'lastDays'; readonly days: number };
/** The selector's value: a preset id, `custom` or `last_days`. */
export type BiPeriodChoice = BiPreset | 'custom' | 'last_days';

export const DEFAULT_PERIOD: BiPeriod = { kind: 'preset', preset: 'last_30_days' };

export function periodChoice(p: BiPeriod): BiPeriodChoice {
  return p.kind === 'preset' ? p.preset : p.kind === 'lastDays' ? 'last_days' : 'custom';
}

/** Whether a custom range can be asked for (real dates, in order). The server enforces the length cap. */
export function customRangeError(from: string, to: string): 'incomplete' | 'invalid' | 'reversed' | undefined {
  if (from === '' || to === '') return 'incomplete';
  if (!isRealDate(from) || !isRealDate(to)) return 'invalid';
  return from > to ? 'reversed' : undefined;
}

/**
 * The local days a period covers (`YYYY-MM-DD`, both inclusive) - what the always-visible from/to dates show for it (owner,
 * 2026-10-12: choosing a preset fills the dates). Presets resolve exactly as the server does (`resolveRelative`: weeks run
 * Monday to Sunday), given `today` in the market's own zone; a typed range is shown as typed; a "last N days" with an
 * impossible N has no days.
 */
export function biPeriodDays(p: BiPeriod, today: string): { readonly from: string; readonly to: string } | undefined {
  if (p.kind === 'custom') return { from: p.from, to: p.to };
  if (p.kind === 'lastDays') return lastDaysValid(p.days) ? resolveRelative('last_days', today, p.days) : undefined;
  return resolveRelative(p.preset as RelativePeriod, today);
}

/**
 * Editing one of the dates: the window is pinned to the range on screen with that one edge changed, as an explicit range
 * (the request contract is unchanged: a custom range is `{ from, to }`), so no preset reads as pressed.
 */
export function editedPeriod(p: BiPeriod, today: string, edge: 'from' | 'to', day: string): BiPeriod {
  const shown = biPeriodDays(p, today) ?? { from: '', to: '' };
  return { kind: 'custom', from: edge === 'from' ? day : shown.from, to: edge === 'to' ? day : shown.to };
}

export function periodRequest(p: BiPeriod): QueryRequest['period'] {
  if (p.kind === 'lastDays') return { relative: 'last_days', days: p.days };
  return p.kind === 'preset' ? { relative: p.preset as RelativePeriod } : { from: p.from, to: p.to };
}

export function lastDaysValid(days: number): boolean {
  return Number.isInteger(days) && days >= 1 && days <= BI_MAX_LAST_DAYS;
}

export function periodValid(p: BiPeriod): boolean {
  if (p.kind === 'lastDays') return lastDaysValid(p.days);
  return p.kind === 'preset' || customRangeError(p.from, p.to) === undefined;
}

/** The chart's time grain for a period: an hourly curve for one day, daily up to a quarter, monthly beyond. */
export function trendGrain(p: BiPeriod, now: Date): Granularity {
  if (p.kind === 'lastDays') return !lastDaysValid(p.days) ? 'day' : p.days <= 1 ? 'hour' : p.days <= 92 ? 'day' : 'month';
  if (p.kind === 'preset') {
    if (p.preset === 'today' || p.preset === 'yesterday') return 'hour';
    if (p.preset === 'last_year') return 'month';
    if (p.preset === 'year_to_date') {
      const start = Date.UTC(now.getUTCFullYear(), 0, 1);
      const days = Math.floor((now.getTime() - start) / 86_400_000) + 1;
      return days > 92 ? 'month' : 'day';
    }
    return 'day';
  }
  if (!periodValid(p)) return 'day';
  const days = inclusiveDays(p.from, p.to);
  return days <= 1 ? 'hour' : days <= 92 ? 'day' : 'month';
}

// ───────────────────────────── scope ─────────────────────────────

/** A market id no market can have: a state with no registered market filters the answer to nothing, never to its whole country. */
const NO_MARKET = 'zz:none';

/** The most values one `in` condition carries (the semantic layer's bound). */
const MAX_IN_VALUES = 100;

/**
 * The filters the operator's scope switcher implies (a city, else a state, else a country, else nothing: the server's reach
 * decides). A STATE filters by the BI's `region` dimension (F3, ADR-0124: `dim_market.region_code`, the market's registered state) - one value, however many
 * cities it has. Only when the semantic layer the console talks to lacks that dimension (`regionDimension: false`: an older API) does it fall back to the
 * `in` list of the markets the catalog places in the state - the same set, capped at {@link MAX_IN_VALUES}.
 */
export function scopeFilters(scope: OperatorScope, markets: readonly OperatingMarketView[] = [], regionDimension = true): readonly QueryFilter[] {
  if (scope.city !== undefined && scope.city !== '') return [{ dimension: 'market', op: 'eq', values: [scope.city] }];
  if (scope.region !== undefined) {
    if (regionDimension) return [{ dimension: 'region', op: 'eq', values: [scope.region.toUpperCase()] }];
    const ids = marketsOfRegion(scope.region, markets).map((m) => m.id).slice(0, MAX_IN_VALUES);
    return [{ dimension: 'market', op: 'in', values: ids.length > 0 ? ids : [NO_MARKET] }];
  }
  if (scope.country !== undefined && scope.country !== '') return [{ dimension: 'country', op: 'eq', values: [scope.country.toUpperCase()] }];
  return [];
}

/** Whether the catalog the server sent offers the state (`region`) dimension; before the catalog is read the answer is yes (nothing is asked until it is). */
export function hasRegionDimension(catalog: { readonly dimensions: readonly { readonly id: string }[] } | undefined): boolean {
  return catalog === undefined || catalog.dimensions.some((d) => d.id === 'region');
}

/** The market ids a query with `scope` should read, given the markets the grants reach (`markets` places a state's markets). */
export function expectedMarkets(scope: OperatorScope, reach: readonly { readonly id: string; readonly country: string; readonly region?: string | undefined }[], markets: readonly OperatingMarketView[] = []): readonly string[] {
  if (scope.city !== undefined && scope.city !== '') return reach.filter((m) => m.id === scope.city).map((m) => m.id);
  if (scope.region !== undefined) {
    // the markets of the state: the reach's own `region` (the catalog the server sent) or the console's market catalog - both name the same state
    const inState = new Set(marketsOfRegion(scope.region, markets).map((m) => m.id));
    const region = scope.region.toUpperCase();
    return reach.filter((m) => inState.has(m.id) || m.region?.toUpperCase() === region).map((m) => m.id);
  }
  if (scope.country !== undefined && scope.country !== '') return reach.filter((m) => m.country.toUpperCase() === scope.country?.toUpperCase()).map((m) => m.id);
  return reach.map((m) => m.id);
}

/** The markets the server left OUT of an answer although the screen asked for them (a narrowed scope is always said aloud). */
export function narrowedMarkets(expected: readonly string[], answered: readonly string[]): readonly string[] {
  const got = new Set(answered);
  return expected.filter((id) => !got.has(id));
}

// ───────────────────────────── comparison ─────────────────────────────

export type BiCompareChoice = 'auto' | 'none' | CompareKind;
export const BI_COMPARE_CHOICES: readonly BiCompareChoice[] = ['auto', 'previous_period', 'same_period_last_year_by_date', 'same_period_last_year_by_weekday', 'up_to_now', 'none'];
export const BI_UP_TO_NOW_BASELINES: readonly CompareAgainst[] = ['last_year_by_date', 'last_year_by_weekday', 'previous_period'];

export interface CompareFields {
  readonly compare?: CompareKind;
  readonly compareAgainst?: CompareAgainst;
}

/** The request fields for a comparison choice. `auto` asks first for the same weekday a year earlier (`resolveAutoCompare` settles it). */
export function compareFields(choice: BiCompareChoice, upToNowAgainst: CompareAgainst = 'last_year_by_date', fallback = false): CompareFields {
  switch (choice) {
    case 'none':
      return {};
    case 'auto':
      return { compare: fallback ? 'previous_period' : 'same_period_last_year_by_weekday' };
    case 'up_to_now':
      return { compare: 'up_to_now', compareAgainst: upToNowAgainst };
    default:
      return { compare: choice };
  }
}

/** True when no figure of the answer has a baseline worth comparing to (every baseline missing or zero). */
export function baselineEmpty(view: Pick<AnalyticsBiQueryView, 'rows' | 'totals'>): boolean {
  const cells: (ResultCell | undefined)[] = [];
  for (const r of view.rows) cells.push(...Object.values(r.baseline ?? {}));
  for (const t of view.totals ?? []) cells.push(...Object.values(t.baseline ?? {}));
  if (cells.length === 0) return true;
  return cells.every((c) => c === undefined || c.value === null || Number(c.value) === 0);
}

/**
 * `auto` comparison: the year-before weekday match is used when it can be placed and has data, else the previous period.
 * `unplaceable` is the planner's `COMPARE_PERIOD_UNPLACEABLE` refusal (the year before is older than the calendar).
 */
export function resolveAutoCompare(first: { readonly refused?: boolean; readonly view?: Pick<AnalyticsBiQueryView, 'rows' | 'totals'> }): 'keep' | 'fallback' {
  if (first.refused === true) return 'fallback';
  return first.view !== undefined && baselineEmpty(first.view) ? 'fallback' : 'keep';
}

// ───────────────────────────── cells and deltas ─────────────────────────────

export type BiUnit = 'count' | 'money' | 'duration_s' | 'distance_m' | 'ratio' | 'decimal' | 'delta_s';

/** What a cell is as a number: a count, a quotient or seconds as Number; an amount stays an exact bigint of minor units. */
export function cellNumber(cell: ResultCell | undefined): number | undefined {
  if (cell === undefined || cell.value === null) return undefined;
  const n = Number(cell.value);
  return Number.isFinite(n) ? n : undefined;
}

export function cellMinor(cell: ResultCell | undefined): bigint | undefined {
  if (cell === undefined || cell.value === null) return undefined;
  try {
    return BigInt(String(cell.value));
  } catch {
    return undefined;
  }
}

export type DeltaKind = 'pct' | 'pp' | 'new' | 'flat' | 'none';
export interface Delta {
  readonly kind: DeltaKind;
  readonly direction: 'up' | 'down' | 'flat';
  /** Percent (pct) or percentage points (pp), one decimal, signed. */
  readonly value: number;
}

const NO_DELTA: Delta = { kind: 'none', direction: 'flat', value: 0 };
const round1 = (n: number): number => Math.round(n * 10) / 10;

/** The change from `base` to `cur`. A ratio changes in points; a zero baseline has no percentage ("new"). */
export function computeDelta(unit: BiUnit, cur: ResultCell | undefined, base: ResultCell | undefined): Delta {
  if (cur === undefined || base === undefined) return NO_DELTA;
  if (unit === 'ratio') {
    const a = cellNumber(cur);
    const b = cellNumber(base);
    if (a === undefined || b === undefined) return NO_DELTA;
    const pp = round1((a - b) * 100);
    return { kind: 'pp', direction: pp > 0 ? 'up' : pp < 0 ? 'down' : 'flat', value: pp };
  }
  if (unit === 'money') {
    const a = cellMinor(cur);
    const b = cellMinor(base);
    if (a === undefined || b === undefined) return NO_DELTA;
    if (b === 0n) return a === 0n ? { kind: 'flat', direction: 'flat', value: 0 } : { kind: 'new', direction: a > 0n ? 'up' : 'down', value: 0 };
    // The difference is exact (bigint); only the final quotient is a float, rounded to one decimal.
    const pct = round1((Number(a - b) / Math.abs(Number(b))) * 100);
    return { kind: 'pct', direction: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat', value: pct };
  }
  const a = cellNumber(cur);
  const b = cellNumber(base);
  if (a === undefined || b === undefined) return NO_DELTA;
  if (b === 0) return a === 0 ? { kind: 'flat', direction: 'flat', value: 0 } : { kind: 'new', direction: a > 0 ? 'up' : 'down', value: 0 };
  const pct = round1(((a - b) / Math.abs(b)) * 100);
  return { kind: 'pct', direction: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat', value: pct };
}

/** Whether a rise is good, bad or neither for a metric. */
export type Polarity = 'up_good' | 'down_good' | 'neutral';

const DOWN_GOOD = new Set([
  'orders_cancelled', 'cancellation_rate', 'rides_no_driver', 'delivery_time', 'prep_time', 'time_to_assign', 'discounts',
  // D531
  'accept_time', 'pickup_time', 'trip_time', 'eta_deviation', 'offer_decline_rate', 'offer_lapse_rate', 'low_rating_share', 'low_ratings', 'issues', 'refunds', 'refund_amount', 'issue_amount', 'store_pauses', 'store_paused_seconds', 'cancelled_value', 'no_driver_value',
]);
const NEUTRAL = new Set(['distance', 'items_sold', 'item_quantity', 'quotes', 'cell_orders', 'ratings', 'eta_compared', 'offers', 'offers_decided', 'promo_redemptions', 'promo_discount', 'promo_gross', 'promo_subsidy', 'promo_customers', 'quoted_value', 'quoted_total', 'supply_online_time', 'supply_idle_time', 'supply_busy_time', 'supply_driver_hours', 'supply_avg_drivers', 'store_live_seconds', 'store_open_seconds']);

export function metricPolarity(metricId: string): Polarity {
  return DOWN_GOOD.has(metricId) ? 'down_good' : NEUTRAL.has(metricId) ? 'neutral' : 'up_good';
}

/** The tone a change is drawn with: a good change `success`, a bad one `danger`, the rest `neutral` (the arrow and the sign carry it too). */
export function deltaTone(metricId: string, delta: Delta): 'success' | 'danger' | 'neutral' {
  if (delta.direction === 'flat' || delta.kind === 'none') return 'neutral';
  const p = metricPolarity(metricId);
  if (p === 'neutral') return 'neutral';
  return (p === 'up_good') === (delta.direction === 'up') ? 'success' : 'danger';
}

// ───────────────────────────── KPI extraction ─────────────────────────────

export interface KpiFigure {
  /** The currency of the figures (money-bound answers), absent for counts and ratios. */
  readonly currency?: string;
  readonly values: Readonly<Record<string, ResultCell>>;
  readonly baseline?: Readonly<Record<string, ResultCell>>;
}

/** The headline figures of an answer: its totals when it asked for them, else its group-less rows (one per currency for money). */
export function kpiFigures(view: Pick<AnalyticsBiQueryView, 'rows' | 'totals'>): readonly KpiFigure[] {
  if (view.totals !== undefined && view.totals.length > 0) {
    return view.totals.map((t: ResultTotal): KpiFigure => ({ ...(t.currency !== undefined ? { currency: t.currency } : {}), values: t.values, ...(t.baseline !== undefined ? { baseline: t.baseline } : {}) }));
  }
  return view.rows.map((r: ResultRow): KpiFigure => ({ ...(r.currency !== undefined ? { currency: r.currency } : {}), values: r.values, ...(r.baseline !== undefined ? { baseline: r.baseline } : {}) }));
}

// ───────────────────────────── trend series ─────────────────────────────

export interface TrendPoint {
  readonly key: string;
  readonly current: number;
  readonly baseline?: number;
}

export interface TrendSeries {
  readonly currency?: string;
  readonly points: readonly TrendPoint[];
}

/** Every local day between two dates, inclusive (a day with no row is a real zero on the chart). */
export function enumerateDays(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = from; d <= to && out.length <= 1100; d = addDays(d, 1)) out.push(d);
  return out;
}

function enumerateMonths(from: string, to: string): string[] {
  const out: string[] = [];
  let y = Number(from.slice(0, 4));
  let m = Number(from.slice(5, 7));
  const endY = Number(to.slice(0, 4));
  const endM = Number(to.slice(5, 7));
  while ((y < endY || (y === endY && m <= endM)) && out.length < 40) {
    out.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

/** The x-axis keys of a trend: the whole window at its grain, plus any key the answer carried that the window does not (a repeated hour). */
export function trendKeys(grain: Granularity, window: { readonly from: string; readonly to: string }, present: readonly string[]): string[] {
  const base = grain === 'day' ? enumerateDays(window.from, window.to) : grain === 'month' ? enumerateMonths(window.from, window.to) : grain === 'hour' ? Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0')) : [];
  const extra = present.filter((k) => !base.includes(k));
  return [...base, ...extra].sort();
}

/**
 * One series per currency (or one, for a count). `convert` turns a cell into the number the chart draws (a major-unit amount for
 * money, the fraction for a ratio); a key the answer lacks is a real zero.
 */
export function trendSeries(
  view: Pick<AnalyticsBiQueryView, 'rows' | 'period'>,
  grain: Granularity,
  metricId: string,
  convert: (cell: ResultCell, currency: string | undefined) => number,
): TrendSeries[] {
  const byCurrency = new Map<string, Map<string, { cur: number; base: number | undefined }>>();
  const present: string[] = [];
  for (const row of view.rows) {
    const key = row.keys[grain]?.key;
    const cell = row.values[metricId];
    if (key === null || key === undefined || cell === undefined) continue;
    present.push(key);
    const currency = row.currency ?? '';
    const map = byCurrency.get(currency) ?? new Map<string, { cur: number; base: number | undefined }>();
    const base = row.baseline?.[metricId];
    map.set(key, { cur: convert(cell, row.currency), base: base !== undefined ? convert(base, row.currency) : undefined });
    byCurrency.set(currency, map);
  }
  const keys = trendKeys(grain, view.period, present);
  const out: TrendSeries[] = [];
  for (const [currency, map] of [...byCurrency.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    out.push({
      ...(currency !== '' ? { currency } : {}),
      points: keys.map((key) => {
        const hit = map.get(key);
        return { key, current: hit?.cur ?? 0, ...(hit?.base !== undefined ? { baseline: hit.base } : {}) };
      }),
    });
  }
  return out;
}

// ───────────────────────────── holidays ─────────────────────────────

export interface HolidayNote {
  readonly side: 'current' | 'baseline';
  readonly marketId: string;
  readonly date: string;
  readonly name: string | null;
}

/**
 * The holidays that fall on ONLY ONE of the two compared windows. A holiday in a market on both sides at the same aligned date is
 * a like-for-like day and is not warned about; a holiday on one side only makes the comparison unfair and is listed.
 */
export function holidayMismatches(holidays: readonly ResultHoliday[] | undefined): readonly HolidayNote[] {
  if (holidays === undefined || holidays.length === 0) return [];
  const current = holidays.filter((h) => h.side === 'current');
  const baseline = holidays.filter((h) => h.side === 'baseline');
  const out: HolidayNote[] = [];
  for (const c of current) {
    if (!baseline.some((b) => b.marketId === c.marketId && b.alignedDate === c.date)) out.push({ side: 'current', marketId: c.marketId, date: c.date, name: c.name });
  }
  for (const b of baseline) {
    if (!current.some((c) => c.marketId === b.marketId && c.date === b.alignedDate)) out.push({ side: 'baseline', marketId: b.marketId, date: b.date, name: b.name });
  }
  return out;
}

// ───────────────────────────── freshness ─────────────────────────────

export interface FreshnessAge {
  readonly unit: 'minute' | 'hour' | 'day';
  readonly amount: number;
  /** Older than a few minutes: the badge turns to a warning (the copy still states the age). */
  readonly stale: boolean;
}

/** How old the data is, in the coarsest whole unit; `undefined` when the server has no aggregate yet. */
export function freshnessAge(f: Pick<AnalyticsBiFreshness, 'ageSeconds'> | undefined): FreshnessAge | undefined {
  const s = f?.ageSeconds;
  if (s === null || s === undefined) return undefined;
  const stale = s > 15 * 60;
  if (s < 3600) return { unit: 'minute', amount: Math.max(0, Math.floor(s / 60)), stale };
  if (s < 86_400) return { unit: 'hour', amount: Math.floor(s / 3600), stale };
  return { unit: 'day', amount: Math.floor(s / 86_400), stale };
}
